import { useEffect, useState } from "react";
import { Loader2, MailCheck, MailX } from "lucide-react";
import { api } from "../../lib/api";
import { fmtDateMtl } from "../../lib/dateMtl";

// Tableau de DÉLIVRABILITÉ des campagnes de prospection (demande du propriétaire,
// 2026-10-08) : envoyés vs rebonds détectés par le robot no-reply, avec taux —
// pour repérer les listes à nettoyer (bouton « Valider les adresses » dans Prospects).
export default function DelivrabiliteCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/rapports/delivrabilite")
      .then(({ data }) => setData(data))
      .catch(() => setData({ campagnes: [], rebonds_sans_campagne: 0 }));
  }, []);

  if (!data) {
    return <p className="text-sm text-ivory/50 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement de la délivrabilité…</p>;
  }
  const camps = data.campagnes || [];
  const totalEnvoyes = camps.reduce((a, c) => a + (c.envoyes || 0), 0);
  const totalRebonds = camps.reduce((a, c) => a + (c.rebonds || 0), 0);
  const totalReponses = camps.reduce((a, c) => a + (c.reponses || 0), 0);
  const tauxGlobal = totalEnvoyes ? (totalRebonds / totalEnvoyes * 100).toFixed(1) : "0.0";
  const tauxRepGlobal = totalEnvoyes ? (totalReponses / totalEnvoyes * 100).toFixed(1) : "0.0";

  return (
    <div className="border border-ivory/15 rounded-xl px-5 py-5 space-y-4" data-testid="delivrabilite-card">
      <div className="flex flex-wrap items-center gap-3">
        <MailCheck size={16} className="text-gold shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <p className="text-sm font-semibold text-ivory">Délivrabilité et engagement des campagnes de prospection</p>
          <p className="text-xs text-ivory/60 mt-0.5">
            Rebonds et réponses détectés automatiquement par la boîte no-reply@trophee.ca. Un taux de
            rebond élevé (&gt; 5 %) signale une liste à nettoyer — onglet Prospects → « Valider les adresses ».
            Le taux de réponse mesure l'engagement réel de chaque campagne.
          </p>
        </div>
        {totalEnvoyes > 0 && (
          <span className="text-xs text-ivory/70" data-testid="delivrabilite-total">
            Rebonds : <strong className={parseFloat(tauxGlobal) > 5 ? "text-red-300" : "text-emerald-400"}>{tauxGlobal} %</strong>
            {" "}· Réponses : <strong className="text-emerald-400">{tauxRepGlobal} %</strong>
            {" "}({totalReponses} réponse(s), {totalRebonds} rebond(s) / {totalEnvoyes} envoyé(s))
          </span>
        )}
      </div>

      {camps.length === 0 ? (
        <p className="text-xs text-ivory/50" data-testid="delivrabilite-vide">
          Aucune campagne de prospection envoyée pour le moment — les taux apparaîtront ici après le premier envoi.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="delivrabilite-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-ivory/45 border-b border-ivory/10">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Campagne</th>
                <th className="px-3 py-2">Sport</th>
                <th className="px-3 py-2 text-right">Envoyés</th>
                <th className="px-3 py-2 text-right">Rebonds</th>
                <th className="px-3 py-2 text-right">Taux</th>
                <th className="px-3 py-2 text-right">Réponses</th>
                <th className="px-3 py-2 text-right">Engagement</th>
              </tr>
            </thead>
            <tbody>
              {camps.map((c) => (
                <tr key={c.id} className="border-b border-ivory/5" data-testid={`delivrabilite-row-${c.id}`}>
                  <td className="px-3 py-2 text-ivory/70 whitespace-nowrap">{fmtDateMtl(c.date)}</td>
                  <td className="px-3 py-2 text-ivory/85 max-w-[280px] truncate" title={c.sujet}>{c.sujet}</td>
                  <td className="px-3 py-2 text-ivory/60">{c.sport}</td>
                  <td className="px-3 py-2 text-right text-ivory/80">{c.envoyes}</td>
                  <td className={`px-3 py-2 text-right ${c.rebonds ? "text-red-300" : "text-emerald-400"}`}>
                    {c.rebonds ? <span className="inline-flex items-center gap-1 justify-end"><MailX size={12} />{c.rebonds}</span> : "0"}
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold ${c.taux > 5 ? "text-red-300" : c.taux > 0 ? "text-amber-300" : "text-emerald-400"}`}>
                    {c.taux} %
                  </td>
                  <td className={`px-3 py-2 text-right ${c.reponses ? "text-emerald-300" : "text-ivory/45"}`}>
                    {c.reponses || 0}
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold ${c.taux_reponses > 0 ? "text-emerald-400" : "text-ivory/45"}`}>
                    {c.taux_reponses} %
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(data.rebonds_sans_campagne > 0 || data.reponses_sans_campagne > 0) && (
        <p className="text-[11px] text-ivory/45">
          + {data.rebonds_sans_campagne} rebond(s) et {data.reponses_sans_campagne} réponse(s) hors
          campagne (envois anciens ou individuels) — voir l'onglet Rebonds.
        </p>
      )}
    </div>
  );
}
